import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import { useAuth } from '@hooks/useAuth';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import MuiButton from '@mui/material/Button';
import { APP_NAME } from '@/config/appConfig';
import Button from '@components/common/atoms/AppButton';
import Icon from '@components/common/atoms/Icon';
import ThemeToggle from '@components/common/atoms/ThemeToggle';
import { NAV_LINKS } from '../../constants/landingPageConstants';

const LandingNavbar: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const primaryCtaLabel = isAuthenticated ? 'Go to Dashboard' : 'Sign In';
  const goToApp = () => navigate(isAuthenticated ? '/dashboard' : '/login');

  const scrollTo = (id: string) => {
    setMobileNavOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <Box component="header" className="sticky top-0 z-50 border-b border-slate-200/70 dark:border-slate-800/70 bg-white/80 dark:bg-slate-950/80 backdrop-blur-xl">
      <Box component="nav" className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
        <MuiButton onClick={() => scrollTo('top')} className="flex items-center gap-3">
          <Box className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-600 to-purple-600 shadow-lg shadow-indigo-500/30">
            <Icon name="Users" size={20} className="text-white" />
          </Box>
          <Box className="text-left">
            <Typography component="span" className="block text-lg font-bold tracking-tight">{APP_NAME}</Typography>
            <Typography component="span" className="block text-[11px] font-medium text-slate-500 dark:text-slate-400 -mt-0.5">
              Human Resource Management
            </Typography>
          </Box>
        </MuiButton>

        <Box className="hidden items-center gap-1 md:flex">
          {NAV_LINKS.map((link) => (
            <MuiButton
              key={link.id}
              onClick={() => scrollTo(link.id)}
              className="rounded-lg px-3 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-100 hover:text-indigo-600 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-indigo-400"
            >
              {link.label}
            </MuiButton>
          ))}
        </Box>

        <Box className="flex items-center gap-2">
          <ThemeToggle />
          <Button variant="primary" size="md" onClick={goToApp} className="hidden sm:inline-flex">
            <Icon name="LogIn" size={16} className="mr-2" />
            {primaryCtaLabel}
          </Button>
          <MuiButton
            onClick={() => setMobileNavOpen((v) => !v)}
            className="rounded-xl p-2.5 text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800 md:hidden"
            aria-label="Toggle navigation"
          >
            <Icon name={mobileNavOpen ? 'X' : 'Menu'} size={22} />
          </MuiButton>
        </Box>
      </Box>

      {/* Mobile menu */}
      {mobileNavOpen && (
        <Box className="border-t border-slate-200/70 px-4 py-3 dark:border-slate-800/70 md:hidden">
          <Box className="flex flex-col gap-1">
            {NAV_LINKS.map((link) => (
              <MuiButton
                key={link.id}
                onClick={() => scrollTo(link.id)}
                className="rounded-lg px-3 py-2.5 text-left text-sm font-medium text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
              >
                {link.label}
              </MuiButton>
            ))}
            <Button variant="primary" size="md" onClick={goToApp} className="mt-2 w-full">
              <Icon name="LogIn" size={16} className="mr-2" />
              {primaryCtaLabel}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default LandingNavbar;
